import { WORKSPACE_PADDING } from '@components/v2/constants';
import { Box, Stack } from '@mui/material';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { RACHEL_CONVERSATION_MAX_WIDTH } from '../../rachel.consts';
import { RACHEL_SCROLLBAR_INSET } from '../../rachel.layout';
import { RachelChatStatus, RachelUIMessage } from '../../rachel.types';
import RachelEmptyConversation from '../RachelEmptyConversation/RachelEmptyConversation';
import RachelMessageItem from '../RachelMessageItem/RachelMessageItem';
import RachelThinkingIndicator from '../RachelThinkingIndicator/RachelThinkingIndicator';
import RachelScrollToBottomButton from './components/RachelScrollToBottomButton/RachelScrollToBottomButton';
import { useRachelConversationList } from './useRachelConversationList';

const SCROLL_TO_BOTTOM_SPACING = 1.5;

const SCROLLBAR_BLEED = WORKSPACE_PADDING - RACHEL_SCROLLBAR_INSET;

interface RachelConversationListProps {
  messages: RachelUIMessage[];
  status: RachelChatStatus;
  onBottomVisibilityChange: (isVisible: boolean) => void;
}

const RachelConversationList = ({
  messages,
  status,
  onBottomVisibilityChange,
}: RachelConversationListProps) => {
  const { translate } = useTranslation();
  const { isAtBottom, newMessagesCount, hasCenteredColumn, scrollToBottom, setScrollNode } =
    useRachelConversationList({
      messages,
      status,
      onBottomVisibilityChange,
    });

  if (messages.length === 0) {
    return <RachelEmptyConversation />;
  }

  const lastMessage = messages[messages.length - 1];
  const isStreamingChat = [RachelChatStatus.SUBMITTED, RachelChatStatus.STREAMING].includes(status);
  const isAwaitingResponse = isStreamingChat && lastMessage?.role === 'user';

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
      }}
    >
      <Box
        ref={setScrollNode}
        data-rachel-text-selection="true"
        sx={(theme) => ({
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarGutter: hasCenteredColumn ? 'stable both-edges' : 'stable',
          width: 'auto',
          py: 2.5,
          marginInline: theme.spacing(-SCROLLBAR_BLEED),
          paddingInline: theme.spacing(SCROLLBAR_BLEED),
        })}
      >
        <Stack
          gap={0.5}
          sx={{ width: '100%', maxWidth: RACHEL_CONVERSATION_MAX_WIDTH, mx: 'auto' }}
        >
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1;
            const isLive = isStreamingChat && isLastMessage && message.role === 'assistant';
            return (
              <RachelMessageItem
                key={message.id}
                message={message}
                isLive={isLive}
                isLastMessage={isLastMessage}
              />
            );
          })}
          {isAwaitingResponse && (
            <RachelThinkingIndicator label={translate(LangEnum.RACHEL_THINKING)} />
          )}
        </Stack>
      </Box>
      {!isAtBottom && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            bottom: theme.spacing(SCROLL_TO_BOTTOM_SPACING),
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
          })}
        >
          <RachelScrollToBottomButton
            newMessagesCount={newMessagesCount}
            onScrollToBottom={scrollToBottom}
          />
        </Box>
      )}
    </Box>
  );
};

export default RachelConversationList;
